import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { useMode } from "../context/ModeContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { NumberTicker } from "../components/motion/NumberTicker";
import { Stagger } from "../components/motion/Stagger";
import { AIThinkingOrb } from "../components/motion/AIThinkingOrb";
import { AssistantChat } from "../components/AssistantChat";
import BrainRecapCard from "../components/BrainRecapCard";
import PixelArtBox from "../components/PixelArtBox";
import {
  Package,
  Sparkles,
  Calendar,
  CheckSquare,
  ArrowRight,
  ArrowUpRight,
  TrendingUp,
  Clock,
  Crown,
  Video,
  Wand2,
  Globe,
  Activity,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const { user, marketingMode, getDisplayTier, token } = useAuth();
  const { mode, config: modeConfig } = useMode();
  const [stats, setStats] = useState(null);
  const [tasksSummary, setTasksSummary] = useState({ in_flight: 0 });
  const [loading, setLoading] = useState(true);

  const displayTier = getDisplayTier();
  const isUltimate = user?.subscription_tier === "ultimate" && !marketingMode;

  // Per-mode dashboard copy. Each realm gets its own hero verb + accent
  // word + subtitles so the dashboard reads like the studio the user is
  // actually in. Falls back to Marketing.
  const MODE_COPY = {
    designer: {
      heroVerb:        "Let's design something",
      heroAccent:      "fresh.",
      shippedTerm:     "designs",
      shippedSubtitle: "designs crafted in the Designing realm",
      enginePrefix:    "Your design engine",
      tokensSubtitle: (tk) => `~${Math.floor(tk / 5)} designs · pure creative play, no store needed`,
    },
    cgi: {
      heroVerb:        "Let's add some",
      heroAccent:      "magic.",
      shippedTerm:     "FX shots",
      shippedSubtitle: "FX shots polished in the CGI realm",
      enginePrefix:    "Your CGI engine",
      tokensSubtitle: (tk) => `~${Math.floor(tk / 10)} FX shots · timeline + Director Polish ready`,
    },
  };
  const copy = MODE_COPY[mode] || MODE_COPY.designer;
  const accent = modeConfig?.accent || "#8B5CF6";

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/dashboard/stats`);
        setStats(r.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Poll the tasks summary so the in-flight pill stays fresh on the dashboard
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const fetchSummary = async () => {
      try {
        const r = await axios.get(`${API}/tasks/me?include_completed=false`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setTasksSummary(r.data?.summary || { in_flight: 0 });
      } catch (e) {
        /* non-fatal */
      }
    };
    fetchSummary();
    const id = setInterval(fetchSummary, 12000);
    return () => { cancelled = true; clearInterval(id); };
  }, [token]);

  const tokenBalance = stats?.tokens || 0;

  const quickActions = [
    { icon: Wand2,     title: "Design something",     desc: "Prompt-to-image in the Studio",      path: "/dashboard/designer",  variant: "default" },
    { icon: Video,     title: "Generate video",       desc: "Kling v2.6 with native audio",        path: "/dashboard/ai-video",  variant: "accent" },
    { icon: Package,   title: "Write an article",     desc: "AI-written company stories",          path: "/dashboard/articles",  variant: "outline" },
    { icon: TrendingUp,title: "Browse Community",     desc: "See what others are shipping",        path: "/dashboard/community", variant: "outline" },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <AIThinkingOrb label="Loading dashboard" size={80} />
      </div>
    );
  }

  return (
    <div className="space-y-10" data-testid="user-dashboard">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-ink-500">
            Hello {user?.name?.split(" ")[0] || "there"}
          </p>
          <h1 className="font-display font-black text-4xl lg:text-5xl tracking-[-0.04em] mt-2 leading-[1]" data-testid="dashboard-hero-text">
            {copy.heroVerb}{" "}
            <span style={{ color: accent, transition: "color 500ms ease-out" }}>
              {copy.heroAccent}
            </span>
          </h1>
        </div>

        {isUltimate ? (
          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-coral-500 via-citrus-500 to-coral-500 rounded-full blur opacity-50 animate-pulse" />
            <div className="relative inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-ink-950 text-white">
              <Crown className="w-4 h-4 text-citrus-500" />
              <span className="text-xs font-bold uppercase tracking-[0.18em]">Ultimate · admin</span>
            </div>
          </div>
        ) : (
          <Badge variant="default" className="!px-4 !py-2 !text-xs">
            {displayTier} plan
          </Badge>
        )}
      </div>

      {/* Live tasks widget — only shown when something is actually running */}
      {tasksSummary.in_flight > 0 && (
        <Link
          to="/dashboard/tasks"
          data-testid="dashboard-tasks-widget"
          className="group flex items-center gap-3 w-fit px-4 py-2.5 rounded-full border border-coral-500/30 bg-coral-500/10 hover:bg-coral-500/15 hover:border-coral-500/50 transition-all"
        >
          <span className="relative inline-flex h-2.5 w-2.5">
            <span className="absolute inset-0 rounded-full bg-coral-500 opacity-75 animate-ping" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-coral-500" />
          </span>
          <span className="text-sm font-semibold text-ink-950">
            <NumberTicker value={tasksSummary.in_flight} duration={0.5} />{" "}
            {tasksSummary.in_flight === 1 ? "task" : "tasks"} running
          </span>
          <Activity className="w-3.5 h-3.5 text-coral-500 group-hover:translate-x-0.5 transition-transform" />
          <ArrowRight className="w-3.5 h-3.5 text-ink-500 opacity-0 group-hover:opacity-100 transition-opacity" />
        </Link>
      )}

      {/* Bento grid */}
      <Stagger className="grid grid-cols-12 gap-5">
        {/* ── Row 1 ──────────────────────────────────────────────────
            Tokens hero (col-6, shrunk from 7 + nudged) + Products (col-3) + PixelArtBox (col-3)
            We dropped "In review" to row 2 per founder spec so the top row reads as
            "your balance · what you have · a creative spark". */}
        <Stagger.Item className="col-span-12 lg:col-span-6">
          <div className="relative h-full p-8 rounded-3xl bg-ink-950 text-white overflow-hidden">
            <div
              className="absolute -top-24 -right-24 w-80 h-80 rounded-full blur-3xl pointer-events-none"
              style={{ background: `${accent}4D`, transition: "background 600ms ease-out" }}
            />
            <div className="absolute -bottom-32 -left-20 w-72 h-72 rounded-full bg-citrus-500/25 blur-3xl pointer-events-none" />
            <div className="relative flex flex-col lg:flex-row gap-8 items-start lg:items-end justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-citrus-500">Token balance</p>
                <p className="font-display font-black text-6xl lg:text-7xl tracking-[-0.05em] leading-none mt-3" data-testid="dashboard-token-balance">
                  {displayTier === "ultimate" ? "∞" : (
                    <NumberTicker value={tokenBalance} />
                  )}
                </p>
                <p className="text-sm font-light text-white/70 mt-3">
                  {displayTier === "ultimate"
                    ? "Unlimited generation. Make it count."
                    : copy.tokensSubtitle(tokenBalance)}
                </p>
              </div>
              <Link to="/dashboard/tokens">
                <Button variant="accent" size="lg" data-testid="topup-tokens-btn">
                  Top up tokens <ArrowUpRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </Stagger.Item>

        <Stagger.Item className="col-span-6 lg:col-span-3">
          <Link
            to="/dashboard/gallery"
            className="block h-full p-7 rounded-3xl bg-white border border-ink-200/70 hover:border-ink-950 transition-all hover:-translate-y-1 hover:shadow-elevated"
          >
            <Package className="w-5 h-5 mb-6" style={{ color: accent }} />
            <p className="font-display font-black text-4xl tracking-[-0.04em]">
              <NumberTicker value={stats?.total_videos || stats?.total_content || 0} />
            </p>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-ink-500 mt-2">My Works</p>
          </Link>
        </Stagger.Item>

        {/* NEW — animated pixel-art "spark" box. Mode-accent palette. */}
        <Stagger.Item className="col-span-6 lg:col-span-3">
          <PixelArtBox />
        </Stagger.Item>

        {/* ── Row 2 ──────────────────────────────────────────────────
            All-time (col-4) + AI engine shrunk (col-5) + In review (col-3).
            "Your AI engine" went from col-7 → col-5; "In review" came up from row 1. */}
        <Stagger.Item className="col-span-12 lg:col-span-4">
          <div className="h-full p-7 rounded-3xl bg-citrus-500/15 border border-citrus-500/40">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-ink-700">All-time generated</p>
              <TrendingUp className="w-5 h-5 text-ink-950" />
            </div>
            <p className="font-display font-black text-6xl tracking-[-0.04em] mt-4">
              <NumberTicker value={stats?.total_content || 0} testid="all-time-content" />
            </p>
            <p className="text-sm text-ink-700 font-light mt-2" data-testid="dashboard-shipped-subtitle">
              {copy.shippedSubtitle}
            </p>
          </div>
        </Stagger.Item>

        <Stagger.Item className="col-span-12 lg:col-span-5">
          <div className="h-full p-6 rounded-3xl bg-white border border-ink-200/70 flex flex-col gap-4">
            <div>
              <p
                className="text-[10px] font-bold uppercase tracking-[0.22em]"
                style={{ color: accent, transition: "color 500ms ease-out" }}
              >
                {copy.enginePrefix}
              </p>
              <h3 className="font-display font-bold text-lg tracking-tight mt-1.5">
                {stats?.image_engine === "gpt-image-1" && "GPT-5.2 Image"}
                {stats?.image_engine === "nano-banana" && "Nano Banana"}
                {!stats?.image_engine && stats?.ai_model === "claude" && "Claude Sonnet"}
                {!stats?.image_engine && stats?.ai_model === "gpt" && "GPT-5.2"}
                {!stats?.image_engine && stats?.ai_model === "gemini" && "Gemini 3 Flash"}
                {!stats?.image_engine && !stats?.ai_model && "Claude Sonnet"}
              </h3>
              <p className="text-xs text-ink-500 font-light mt-1.5">
                Nano Banana imagery · Kling v2.6 video with native audio.
              </p>
            </div>
            <Link to="/dashboard/subscription" className="self-start">
              <Button variant="outline" size="sm" data-testid="upgrade-btn">
                Upgrade plan <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </Stagger.Item>

        <Stagger.Item className="col-span-12 lg:col-span-3">
          <Link
            to="/dashboard/tasks"
            className="block h-full p-7 rounded-3xl bg-white border border-ink-200/70 hover:border-ink-950 transition-all hover:-translate-y-1 hover:shadow-elevated"
          >
            <Clock className="w-5 h-5 mb-6" style={{ color: accent }} />
            <p className="font-display font-black text-4xl tracking-[-0.04em]">
              <NumberTicker value={tasksSummary?.in_flight || 0} />
            </p>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-ink-500 mt-2">Rendering now</p>
          </Link>
        </Stagger.Item>
      </Stagger>

      {/* XEA Assistant — middle-of-dashboard chat bar */}
      <section data-testid="assistant-chat-section">
        <AssistantChat variant="bar" />
      </section>

      {/* Brain Recap — only renders once the brain has ≥ 3 events */}
      <BrainRecapCard />

      {/* Quick actions */}
      <div>
        <div className="flex items-end justify-between mb-5">
          <h2 className="font-display font-black text-3xl tracking-[-0.03em]">Pick where to start</h2>
          <Link
            to="/dashboard/gallery"
            className="text-xs font-bold uppercase tracking-[0.22em] text-ink-500 hover:text-ink-950 inline-flex items-center gap-2"
          >
            See gallery <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        <Stagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {quickActions.map((a, i) => (
            <Stagger.Item key={i}>
              <Link
                to={a.path}
                data-testid={`quick-action-${a.title.toLowerCase().replace(/\s/g, "-")}`}
                className={`group block h-full p-7 rounded-3xl transition-all hover:-translate-y-1
                  ${a.variant === "default"
                    ? "bg-ink-950 text-white hover:shadow-[0_20px_48px_rgba(10,10,10,0.24)]"
                    : a.variant === "accent"
                    ? "bg-coral-500 text-white hover:shadow-coral"
                    : "bg-white border border-ink-200/70 hover:border-ink-950 hover:shadow-elevated"}
                `}
              >
                <a.icon className="w-6 h-6 mb-6" />
                <h3 className="font-display font-bold text-xl tracking-tight leading-tight">{a.title}</h3>
                <p className={`text-sm font-light mt-2 ${a.variant === "outline" ? "text-ink-500" : "text-white/70"}`}>
                  {a.desc}
                </p>
                <span className="mt-6 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] opacity-0 group-hover:opacity-100 transition-opacity">
                  Open <ArrowRight className="w-3 h-3" />
                </span>
              </Link>
            </Stagger.Item>
          ))}
        </Stagger>
      </div>
    </div>
  );
};

export default DashboardPage;
